// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
  extrato_vs_s26: 'Extrato bancário ≠ S-26 (§55)',
  s30_vs_s26: 'S-30 ≠ S-26 (§42/§50)',
  saldo_carryover: 'Saldo de carryover (§47)',
  donativos_nao_zero: 'Donativos ≠ 0 (§43)',
  outra: 'Outra (descrição livre)',
};

/**
 * Modal to quickly file a new discrepancy. Optionally pre-fills period_key /
 * month / rule_ref when triggered from the AuditChecklistPage validation card.
 */
export default function DiscrepancyModal({
  open, onOpenChange, defaultPeriodKey, defaultMonth, defaultRuleRef, defaultType,
  onCreated,
}) {
  const [type, setType] = useState(defaultType || 'extrato_vs_s26');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [valueDiff, setValueDiff] = useState('');
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setType(defaultType || 'extrato_vs_s26');
    setTitle('');
    setDescription('');
    setValueDiff('');
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error('Informe um título');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        period_key: defaultPeriodKey || null,
        month: defaultMonth || null,
        type,
        title: title.trim(),
        description: description.trim() || null,
        value_diff: valueDiff === '' ? null : Number(valueDiff),
        rule_ref: defaultRuleRef || null,
      };
      const res = await axios.post(`${API}/discrepancies`, payload);
      toast.success('Discrepância registrada');
      onCreated?.(res.data);
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao registrar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-lg" data-testid="discrepancy-modal">
        <DialogHeader>
          <DialogTitle>Registrar discrepância</DialogTitle>
          <DialogDescription>
            S-27 §53/§55/§56/§61 — registre divergências encontradas durante a auditoria.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">Tipo</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="discrepancy-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(TYPE_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">Título *</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex: Divergência R$ 50 no extrato de outubro"
              data-testid="discrepancy-title-input"
            />
          </div>

          <div>
            <Label className="text-xs">Valor da diferença (R$)</Label>
            <Input
              type="number"
              step="0.01"
              value={valueDiff}
              onChange={(e) => setValueDiff(e.target.value)}
              placeholder="0,00"
              data-testid="discrepancy-value-input"
            />
          </div>

          <div>
            <Label className="text-xs">Descrição detalhada</Label>
            <Textarea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="O que foi encontrado, em qual documento, providências sugeridas..."
              data-testid="discrepancy-description-textarea"
            />
          </div>

          {(defaultPeriodKey || defaultMonth || defaultRuleRef) && (
            <p className="text-xs text-muted-foreground">
              Vinculado a: {defaultPeriodKey ? `trimestre ${defaultPeriodKey}` : ''}
              {defaultMonth ? ` · mês ${defaultMonth}` : ''}
              {defaultRuleRef ? ` · regra ${defaultRuleRef}` : ''}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="discrepancy-save-button">
            Registrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
